import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { handleErrorMessage } from "../../../../helpers/methods";
import {
  getRelativeFontSize,
  mediumFont,
  regularFont,
  theme,
} from "../../../../utils/styles";
import { getVedicAspectsData } from "../../AnalysisService";

interface CustomProps {
  profileId: string;
  setIsLoading: Function;
}

interface AspectSection {
  title: string;
  note: string;
  columns: { key: string; label: string }[];
  rows: Record<string, any>[];
}

interface AspectsData {
  graha_drishti?: AspectSection;
  graha_drishti_full?: AspectSection;
  lord_to_house?: AspectSection;
  lord_to_lord?: AspectSection;
  planet_to_lord?: AspectSection;
  planets_to_house?: AspectSection;
  rashi_drishti?: AspectSection;
  western_aspects?: AspectSection;
}

const SECTION_ORDER: (keyof AspectsData)[] = [
  "graha_drishti",
  "graha_drishti_full",
  "rashi_drishti",
  "planets_to_house",
  "lord_to_house",
  "lord_to_lord",
  "planet_to_lord",
  "western_aspects",
];

// ── section metadata ────────────────────────────────────────────────────────

interface SectionMeta {
  symbol: string;
  accent: string;
  gradientFrom: string;
  gradientTo: string;
}

const SECTION_META: Record<string, SectionMeta> = {
  graha_drishti: {
    symbol: "☉",
    accent: "#9053F6",
    gradientFrom: "rgba(144,83,246,0.28)",
    gradientTo: "rgba(99,102,241,0.06)",
  },
  graha_drishti_full: {
    symbol: "♃",
    accent: "#6366F1",
    gradientFrom: "rgba(99,102,241,0.28)",
    gradientTo: "rgba(139,92,246,0.06)",
  },
  lord_to_house: {
    symbol: "♋",
    accent: "#3B82F6",
    gradientFrom: "rgba(59,130,246,0.26)",
    gradientTo: "rgba(99,102,241,0.06)",
  },
  lord_to_lord: {
    symbol: "☌",
    accent: "#EC4899",
    gradientFrom: "rgba(236,72,153,0.22)",
    gradientTo: "rgba(139,92,246,0.06)",
  },
  planet_to_lord: {
    symbol: "☿",
    accent: "#F59E0B",
    gradientFrom: "rgba(245,158,11,0.22)",
    gradientTo: "rgba(144,83,246,0.06)",
  },
  planets_to_house: {
    symbol: "△",
    accent: "#10B981",
    gradientFrom: "rgba(16,185,129,0.22)",
    gradientTo: "rgba(99,102,241,0.06)",
  },
  rashi_drishti: {
    symbol: "☾",
    accent: "#A78BFA",
    gradientFrom: "rgba(167,139,250,0.26)",
    gradientTo: "rgba(236,72,153,0.06)",
  },
  western_aspects: {
    symbol: "☍",
    accent: "#06B6D4",
    gradientFrom: "rgba(6,182,212,0.22)",
    gradientTo: "rgba(99,102,241,0.06)",
  },
};

const DEFAULT_META: SectionMeta = {
  symbol: "✦",
  accent: "#9053F6",
  gradientFrom: "rgba(144,83,246,0.28)",
  gradientTo: "rgba(99,102,241,0.06)",
};

// ── colors ──────────────────────────────────────────────────────────────────

const CATEGORY_PALETTE: Record<
  string,
  { bg: string; border: string; text: string; glow: string }
> = {
  Full: {
    bg: "rgba(76,175,80,0.16)",
    border: "rgba(76,175,80,0.55)",
    text: "#66BB6A",
    glow: "rgba(76,175,80,0.28)",
  },
  Special: {
    bg: "rgba(206,147,216,0.16)",
    border: "rgba(206,147,216,0.55)",
    text: "#CE93D8",
    glow: "rgba(206,147,216,0.28)",
  },
  Partial: {
    bg: "rgba(255,152,0,0.16)",
    border: "rgba(255,152,0,0.55)",
    text: "#FFA726",
    glow: "rgba(255,152,0,0.28)",
  },
};

const STRENGTH_CONFIG: Record<string, { color: string; value: number }> = {
  "100%": { color: "#4CAF50", value: 100 },
  "75%": { color: "#8BC34A", value: 75 },
  "50%": { color: "#FF9800", value: 50 },
  "25%": { color: "#FF5722", value: 25 },
};

const getCategoryStyle = (cat: string) =>
  CATEGORY_PALETTE[cat] ?? {
    bg: "rgba(255,255,255,0.07)",
    border: "rgba(255,255,255,0.22)",
    text: "#FFFFFF",
    glow: "transparent",
  };

// ── small components ────────────────────────────────────────────────────────

const AstroSymbol = ({
  symbol,
  accent,
}: {
  symbol: string;
  accent: string;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 44,
      height: 44,
      borderRadius: "13px",
      background: `${accent}28`,
      border: `1px solid ${accent}55`,
      boxShadow: `0 0 18px ${accent}44, inset 0 1px 0 rgba(255,255,255,0.1)`,
      flexShrink: 0,
      fontSize: 22,
      lineHeight: 1,
      fontFamily:
        '"Segoe UI Symbol", "Apple Color Emoji", "Noto Sans Symbols", serif',
      color: accent,
      userSelect: "none",
      [theme.breakpoints.down("md")]: {
        height: 32,
        width: 32,
        fontSize: 16,
      },
    }}
  >
    {symbol}
  </Box>
);

const CategoryChip = ({ label }: { label: string }) => {
  const p = getCategoryStyle(label);
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        ...mediumFont,
        fontSize: getRelativeFontSize(-1),
        height: 24,
        backgroundColor: p.bg,
        color: p.text,
        border: `1px solid ${p.border}`,
        boxShadow: `0 0 8px ${p.glow}`,
        "& .MuiChip-label": { px: 1.25 },
        [theme.breakpoints.down("md")]: {
          fontSize: getRelativeFontSize(-3),
        },
      }}
    />
  );
};

const StrengthBadge = ({ value }: { value: string }) => {
  const cfg = STRENGTH_CONFIG[value] ?? { color: "#FFFFFF", value: 0 };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.5,
        minWidth: 70,
      }}
    >
      <Box
        sx={{
          px: 1.25,
          py: 0.3,
          borderRadius: "12px",
          backgroundColor: `${cfg.color}22`,
          border: `1px solid ${cfg.color}66`,
          color: cfg.color,
          ...mediumFont,
          fontSize: getRelativeFontSize(-1),
          whiteSpace: "nowrap",
          boxShadow: `0 0 8px ${cfg.color}44`,
          [theme.breakpoints.down("md")]: {
            fontSize: getRelativeFontSize(-3),
          },
        }}
      >
        {value}
      </Box>
      <LinearProgress
        variant="determinate"
        value={cfg.value}
        sx={{
          width: "100%",
          height: 3,
          borderRadius: 4,
          backgroundColor: `${cfg.color}20`,
          "& .MuiLinearProgress-bar": {
            backgroundColor: cfg.color,
            borderRadius: 4,
          },
        }}
      />
    </Box>
  );
};

const PlanetChip = ({ name }: { name: string }) => (
  <Chip
    label={name}
    size="small"
    sx={{
      ...regularFont,
      fontSize: getRelativeFontSize(-2),
      height: 22,
      backgroundColor: "rgba(144,83,246,0.18)",
      color: "#D8B4FE",
      border: "1px solid rgba(144,83,246,0.48)",
      boxShadow: "0 0 6px rgba(144,83,246,0.22)",
      "& .MuiChip-label": { px: 0.75 },
      [theme.breakpoints.down("md")]: {
        fontSize: getRelativeFontSize(-3),
      },
    }}
  />
);

// ── array renderers ─────────────────────────────────────────────────────────

const renderStringArray = (items: string[]) => {
  if (items.length === 0)
    return (
      <Typography
        sx={{
          color: "rgba(255,255,255,0.22)",
          fontSize: getRelativeFontSize(-1),
        }}
      >
        —
      </Typography>
    );
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5} justifyContent="center">
      {items.map((p, i) => (
        <PlanetChip key={i} name={p} />
      ))}
    </Stack>
  );
};

const renderObjectArray = (items: any[]) => {
  if (items.length === 0)
    return (
      <Typography
        sx={{
          color: "rgba(255,255,255,0.22)",
          fontSize: getRelativeFontSize(-1),
        }}
      >
        —
      </Typography>
    );
  return (
    <Stack spacing={0.75} alignItems="center">
      {items.map((item: any, i: number) => {
        const p = getCategoryStyle(item.category);
        const info = item.house
          ? `${item.house} · ${item.sign}`
          : `${item.aspected_sign} → ${item.lord} (${item.lord_house})`;
        return (
          <Box
            key={i}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.4,
              borderRadius: "20px",
              backgroundColor: p.bg,
              border: `1px solid ${p.border}`,
              boxShadow: `0 0 8px ${p.glow}`,
              whiteSpace: "nowrap",
            }}
          >
            <Typography
              sx={{
                ...mediumFont,
                fontSize: getRelativeFontSize(-2),
                color: p.text,
              }}
            >
              {item.strength}
            </Typography>
            <Box sx={{ width: 1, height: 12, backgroundColor: p.border }} />
            <Typography
              sx={{
                ...regularFont,
                fontSize: getRelativeFontSize(-2),
                color: "rgba(255,255,255,0.8)",
              }}
            >
              {info}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
};

// ── cell renderer ───────────────────────────────────────────────────────────

const renderCell = (colKey: string, value: any) => {
  if (Array.isArray(value)) {
    return typeof value[0] === "string" || value.length === 0
      ? renderStringArray(value)
      : renderObjectArray(value);
  }
  if (value == null)
    return (
      <Typography sx={{ color: "rgba(255,255,255,0.22)", fontSize: "inherit" }}>
        —
      </Typography>
    );
  const str = String(value);
  if (colKey === "category") return <CategoryChip label={str} />;
  if (colKey === "strength") return <StrengthBadge value={str} />;
  return str;
};

// ── section renderer ────────────────────────────────────────────────────────

const renderSection = (sectionKey: string, section: AspectSection) => {
  const meta = SECTION_META[sectionKey] ?? DEFAULT_META;
  const { symbol, accent, gradientFrom, gradientTo } = meta;

  return (
    <Box
      sx={{
        borderRadius: "20px",
        border: `1px solid ${accent}38`,
        background: "rgba(10,8,24,0.7)",
        // overflow: clip visually clips children at border-radius (rounded corners)
        // WITHOUT creating a scroll container — so position:sticky inside still works
        // relative to the Analysis main scroll container.
        overflow: "clip",
        boxShadow: `0 2px 24px ${accent}1a, inset 0 1px 0 rgba(255,255,255,0.05)`,
        transition: "box-shadow 0.3s ease",
        "&:hover": {
          boxShadow: `0 6px 32px ${accent}2e, inset 0 1px 0 rgba(255,255,255,0.07)`,
        },
      }}
    >
      {/*
        Sticky section header.
        - Sits OUTSIDE the overflowX:auto table wrapper so it is not trapped by it.
        - Has a fully opaque background so scrolling table rows don't bleed through.
        - position:sticky + top:0 makes it pin to the Analysis scroll container's top
          until the section card scrolls entirely off screen.
      */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        {/* Top accent line — part of the sticky block */}
        <Box
          sx={{
            height: 3,
            background: `linear-gradient(90deg, ${accent} 0%, ${accent}88 55%, transparent 100%)`,
          }}
        />

        {/* Header body — must be opaque */}
        <Box
          sx={{
            px: 3,
            py: 2.25,
            // Solid dark base ensures rows below never bleed through while scrolling
            backgroundColor: "#0c0a1e",
            backgroundImage: `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientTo} 100%)`,
            borderBottom: `1px solid ${accent}30`,
            [theme.breakpoints.down("md")]: {
              p: 1,
            },
          }}
        >
          {/* Title row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <AstroSymbol symbol={symbol} accent={accent} />
              <Typography
                sx={{
                  ...mediumFont,
                  fontSize: getRelativeFontSize(4),
                  color: "#FFFFFF",
                  letterSpacing: "0.02em",
                  lineHeight: 1.2,
                  [theme.breakpoints.down("md")]: {
                    fontSize: getRelativeFontSize(0),
                  },
                }}
              >
                {section.title}
              </Typography>
            </Box>

            {/* Aspects count badge */}
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 0.5,
                px: 1.5,
                py: 0.6,
                borderRadius: "10px",
                backgroundColor: `${accent}1a`,
                border: `1px solid ${accent}44`,
                boxShadow: `0 0 10px ${accent}28`,
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  ...mediumFont,
                  fontSize: getRelativeFontSize(2),
                  color: accent,
                  lineHeight: 1,
                  [theme.breakpoints.down("md")]: {
                    fontSize: getRelativeFontSize(-1),
                  },
                }}
              >
                {section.rows.length}
              </Typography>
              <Typography
                sx={{
                  ...regularFont,
                  fontSize: getRelativeFontSize(-2),
                  color: `${accent}bb`,
                  lineHeight: 1,
                  [theme.breakpoints.down("md")]: {
                    fontSize: getRelativeFontSize(-3),
                  },
                }}
              >
                aspects
              </Typography>
            </Box>
          </Box>

          {/* Note row */}
          <Box
            sx={{
              ml: "58px",
              pl: 1.5,
              borderLeft: `2px solid ${accent}38`,
              display: "flex",
              alignItems: "flex-start",
              gap: 0.5,
            }}
          >
            <InfoOutlinedIcon
              sx={{
                color: `${accent}77`,
                fontSize: 13,
                mt: "2px",
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                ...regularFont,
                fontSize: getRelativeFontSize(-1),
                color: "rgba(255,255,255,0.45)",
                lineHeight: 1.6,
                [theme.breakpoints.down("md")]: {
                  fontSize: getRelativeFontSize(-4),
                },
              }}
            >
              {section.note}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/*
        Table wrapper — overflowX:auto handles wide tables.
        This creates a horizontal scroll container but NOT a vertical one,
        so the sticky header above is unaffected.
      */}
      <Box
        sx={{
          overflowX: "auto",
          "&::-webkit-scrollbar": { height: 5 },
          "&::-webkit-scrollbar-track": {
            background: "rgba(255,255,255,0.03)",
          },
          "&::-webkit-scrollbar-thumb": {
            background: `${accent}44`,
            borderRadius: 3,
          },
          "&::-webkit-scrollbar-thumb:hover": { background: `${accent}88` },
        }}
      >
        <Table
          sx={{ borderCollapse: "collapse", width: "100%", minWidth: 480 }}
          size="small"
        >
          <TableHead>
            <TableRow>
              {section.columns.map((col, colIdx) => (
                <TableCell
                  key={col.key}
                  sx={{
                    ...mediumFont,
                    fontSize: getRelativeFontSize(-1),
                    color: accent,
                    backgroundColor: `${accent}18`,
                    border: "none",
                    borderBottom: `2px solid ${accent}44`,
                    borderLeft: colIdx === 0 ? `3px solid ${accent}88` : "none",
                    padding: "10px 16px",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    [theme.breakpoints.down("md")]: {
                      fontSize: getRelativeFontSize(-3),
                      p: 1,
                    },
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {section.rows.map((row, rowIdx) => {
              const catKey = row["category"] as string | undefined;
              const catStyle = catKey ? getCategoryStyle(catKey) : null;
              const leftBorderColor = catStyle
                ? catStyle.border
                : `${accent}30`;

              return (
                <TableRow
                  key={rowIdx}
                  sx={{
                    backgroundColor:
                      rowIdx % 2 === 0
                        ? "rgba(255,255,255,0.022)"
                        : "transparent",
                    "&:hover": { backgroundColor: `${accent}16` },
                    "&:last-child td": { borderBottom: "none" },
                    transition: "background-color 0.15s ease",
                  }}
                >
                  {section.columns.map((col, colIdx) => (
                    <TableCell
                      key={col.key}
                      sx={{
                        ...regularFont,
                        fontSize: getRelativeFontSize(0),
                        color: "#FFFFFF",
                        border: "none",
                        borderBottom: "1px solid rgba(255,255,255,0.04)",
                        borderLeft:
                          colIdx === 0
                            ? `3px solid ${leftBorderColor}`
                            : "none",
                        padding: "11px 16px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        [theme.breakpoints.down("md")]: {
                          fontSize: getRelativeFontSize(-3),
                          p: 1,
                        },
                      }}
                    >
                      {renderCell(col.key, row[col.key])}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

// ── component ───────────────────────────────────────────────────────────────

const Aspects = (props: CustomProps) => {
  const [data, setData] = useState<AspectsData | undefined>();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (props.profileId) {
      fetchAspectsData();
    }
  }, [props.profileId, i18n.language]);

  const fetchAspectsData = async () => {
    try {
      props.setIsLoading(true);
      const language = i18n.language.toLowerCase();
      const response = await getVedicAspectsData(props.profileId, language);
      setData(response.tables);
      props.setIsLoading(false);
    } catch (error: any) {
      props.setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  return (
    <Stack
      direction="column"
      spacing={3}
      py={2}
      sx={{ minWidth: 0, width: { lg: "calc(100vw - 470px)", md: "auto" } }}
    >
      {SECTION_ORDER.map((key) => {
        const section = data?.[key];
        if (!section) return null;
        return (
          <Box key={key} sx={{ minWidth: 0 }}>
            {renderSection(key, section)}
          </Box>
        );
      })}
    </Stack>
  );
};

export default Aspects;
